import { LockClockRounded } from "@mui/icons-material";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { API, action, controller } from "../../../api/api";
import { Button, InputCheck, InputOtp } from "../../../controls";
import { Form } from "../../../layout";
import { ToFormData } from "../../../hooks/Method";

/**
 * Second step of a two-factor sign-in.
 *
 * The password step returned a challenge token but NO session cookie — nothing is
 * signed in until this component gets a 200 back. `challenge.method` is 2 for an
 * emailed code and 1 for an authenticator app; only the email flow can resend.
 */
const TwoFactorVerify = ({ challenge, onVerified, onCancel }) => {
  const { t } = useTranslation(["common", "validation"]);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);

  // Held locally because switching to an emailed code REPLACES the challenge in
  // place — same token, different method. Keeping it in state lets the screen
  // re-render as an email prompt without restarting the sign-in.
  const [ch, setCh] = useState(challenge);
  useEffect(() => setCh(challenge), [challenge]);

  // The cooldown is tracked as a DEADLINE, not a decrementing counter. A counter
  // driven by setInterval loses time whenever the browser throttles a background
  // tab — which is exactly what happens here, because the user leaves to go and
  // read the email. Measuring against a fixed timestamp stays correct no matter
  // how the tab is treated.
  const initialCooldown = challenge?.resendAfterSeconds || 0;
  const [resendAt, setResendAt] = useState(() => Date.now() + initialCooldown * 1000);
  const [resendTotal, setResendTotal] = useState(initialCooldown);
  const [now, setNow] = useState(() => Date.now());

  const cooldown = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const isEmail = Number(ch?.method) === 2;

  const startCooldown = (seconds) => {
    const s = Number(seconds) || 0;
    setResendTotal(s);
    setResendAt(Date.now() + s * 1000);
    setNow(Date.now());
  };

  // Ticks twice a second so the progress ring moves smoothly rather than in
  // visible one-second jumps, and stops itself once the deadline passes.
  useEffect(() => {
    if (resendAt <= Date.now()) return undefined;
    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= resendAt) clearInterval(id);
    }, 500);
    return () => clearInterval(id);
  }, [resendAt]);

  // m:ss — "Resend in 178s" is unreadable at a three-minute cooldown.
  const clock = (secs) =>
    `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;

  const submit = (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setError(t("validation:verificationCodeRequired", "Enter the verification code"));
      return;
    }
    setLoading(true);
    setError("");
    setInfo("");

    API(controller.Auth + action.TwoFactorVerify).post(
      ToFormData({
        challengeToken: ch.challengeToken,
        code: code.trim(),
        trustDevice,
      }),
      (res) => {
        setLoading(false);
        if (!res) return;
        if (res.status === 200) {
          onVerified(res.result);
          return;
        }
        setError(res.msg || t("twoFactorInvalidCode", "That code is not correct."));
        // Clear the boxes so the next attempt starts from the first digit;
        // InputOtp re-focuses itself when the value empties.
        setCode("");
      },
    );
  };

  const resend = () => {
    if (cooldown > 0) return;
    setLoading(true);
    setError("");
    API(controller.Auth + action.TwoFactorResend).post(
      ToFormData({ challengeToken: ch.challengeToken }),
      (res) => {
        setLoading(false);
        if (!res) return;
        if (res.status === 200) {
          setInfo(res.msg);
          startCooldown(ch?.resendAfterSeconds);
        } else {
          setError(res.msg);
        }
      },
    );
  };

  // Swap an authenticator-app challenge for an emailed code. The server keeps the
  // same challenge token and returns the updated challenge, so the sign-in carries
  // on rather than starting over.
  const useEmailInstead = () => {
    setLoading(true);
    setError("");
    setInfo("");
    API(controller.Auth + action.TwoFactorUseEmail).post(
      ToFormData({ challengeToken: ch.challengeToken }),
      (res) => {
        setLoading(false);
        if (!res) return;
        if (res.status === 200) {
          setCh(res.result);
          setCode("");
          // Deliberately no info banner. Switching method rewrites the hint above
          // to "We sent a 6-digit code to …", so echoing the server's near-identical
          // "A code is on its way to …" underneath it just said the same thing twice.
          // Resend still sets one, because there the hint does not change and the
          // banner is the only sign anything happened.
          setInfo("");
          startCooldown(res.result?.resendAfterSeconds);
        } else {
          setError(res.msg);
        }
      },
    );
  };

  return (
    <Form noValidate onSubmit={submit}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <div className="tx-center mb-10 mt-20 d-flex j-center">
            <div
              className="w-fit p-5"
              style={{ backgroundColor: "var(--primaryXS-color)", borderRadius: "50%" }}
            >
              <LockClockRounded className="primary-color" sx={{ fontSize: "80px" }} />
            </div>
          </div>
        </Grid>

        <Grid item xs={12} className="tx-center">
          <Typography variant="h6">
            {t("twoFactorTitle", "Two-step verification")}
          </Typography>
          <Typography variant="body2" className="gray-color mt-5">
            {isEmail
              ? t("twoFactorEmailHint", "We sent a 6-digit code to {{email}}.", {
                  email: ch?.sentTo || "",
                })
              : t(
                  "twoFactorAppHint",
                  "Open your authenticator app and enter the 6-digit code.",
                )}
          </Typography>
        </Grid>

        {error && (
          <Grid item xs={12} className="tx-center">
            <Typography color="error">{error}</Typography>
          </Grid>
        )}
        {info && (
          <Grid item xs={12} className="tx-center">
            <Typography color="primary">{info}</Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <InputOtp
            name="code"
            value={code}
            onChange={setCode}
            autoFocus
            invalid={!!error}
          />
        </Grid>

        {/* Tied to the boxes above rather than floating a full 24px gutter away —
            it is an option ABOUT the code you just typed, not a separate step. */}
        {ch?.canTrustDevice && (
          <Grid item xs={12} className="!pt-[10px]">
            <div className="d-flex j-center">
              <InputCheck
                label={t("trustThisDevice", "Don't ask again on this device")}
                name="trustDevice"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
              />
            </div>
          </Grid>
        )}

        <Grid item xs={12}>
          <Button loading={loading} type="submit" fullWidth size="large">
            {t("verify", "Verify")}
          </Button>
        </Grid>

        {/* Real buttons, not click-handling spans: they inherit the theme's focus
            ring, hover and disabled states, and match the Back action on the
            forgot-password screen. `disabled` is what greys the resend during
            its cooldown, instead of hand-rolled colour classes. */}
        <Grid item xs={12} className="!pt-[6px] tx-center grid gap-1">
          {/* While cooling down this is NOT a disabled button with changing text —
              the label's width jumped every tick, and a greyed button reads as
              broken rather than as "waiting". It becomes a quiet status line with a
              depleting ring, then turns back into a real button when it is usable. */}
          {isEmail &&
            (cooldown > 0 ? (
              <div
                className="d-flex j-center a-center"
                style={{ gap: 8, padding: "9px 0" }}
              >
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  {/* Track ring behind the value ring — determinate progress has no
                      track of its own, and without it the arc floats in space. */}
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={18}
                    thickness={5}
                    sx={{ color: "var(--primaryXS-color)" }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={resendTotal > 0 ? (cooldown / resendTotal) * 100 : 0}
                    size={18}
                    thickness={5}
                    sx={{
                      color: "var(--primary-color)",
                      position: "absolute",
                      left: 0,
                    }}
                  />
                </Box>
                <span style={{ fontSize: 14, color: "#6b7280" }}>
                  {t("resendAvailableIn", "Resend code in")}{" "}
                  <span
                    style={{
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: 600,
                      color: "#4b5563",
                    }}
                  >
                    {clock(cooldown)}
                  </span>
                </span>
              </div>
            ) : (
              <Button
                variant="text"
                size="medium"
                fullWidth
                disabled={loading}
                onClick={resend}
              >
                {t("resendCode", "Resend code")}
              </Button>
            ))}

          {/* The way out when the phone is not to hand. Offered only for an app
              challenge, only when the tenant permits email codes and the account
              has an address — the server checks all three again. */}
          {ch?.canUseEmailFallback && (
            <Button
              variant="text"
              size="medium"
              fullWidth
              disabled={loading}
              onClick={useEmailInstead}
            >
              {t("useEmailInstead", "Can't use your app? Email a code to {{email}}", {
                email: ch?.fallbackEmail || "",
              })}
            </Button>
          )}

          <Button
            variant="text"
            size="medium"
            fullWidth
            onClick={onCancel}
            className="!text-gray-500"
          >
            {t("backToLogin", "Back to sign in")}
          </Button>
        </Grid>
      </Grid>
    </Form>
  );
};

export default TwoFactorVerify;
